import apis from '@api/client';
import { MeEmployeeRequiredActionsEnum } from '@api/dto';
import ErrorDialog from '@components/ErrorDialog';
import MessageLayout from '@components/MessageLayout';
import { APP_URL, USE_BLS_THEME } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button, CircularProgress } from '@mui/material';
import { useRequireAuthentication } from '@pages/RegisterPage/hooks/useRequireAuthentication';
import { resolveCommonError } from '@pages/RegisterPage/utils/errorCodeUtils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { resolveUrl } from '~/utils/urlUtils';

import CompleteDataForm from './CompleteDataForm';

export function CompleteDataDuringRegistration() {
  const { authenticated } = useAuth();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { registrationCode } = useRegisterContext();
  const employeeId = state?.employeeId;

  useEffect(() => {
    if (!registrationCode) {
      void navigate('/');
      return;
    }
    if (!authenticated) {
      void navigate('/auth');
      return;
    }
  }, [authenticated, navigate, registrationCode]);

  return <CompleteDataForm employeeId={employeeId} />;
}

export function CompleteDataAfterRegistration() {
  const { t } = useTranslation();
  const { updateCompanyId } = useRegisterContext();
  const { authenticated } = useAuth();
  const { loginAgain } = useRequireAuthentication();
  const [error, setError] = useState<string | null>(null);

  const getIncompleteEmployee = useQuery({
    queryKey: [apis.me.listMyEmployees.name],
    queryFn: () => apis.me.listMyEmployees().then(res => res.data),
    select: data => data.items.find(emp => emp.requiredActions.some(action => action === MeEmployeeRequiredActionsEnum.UpdateProfile)),
    enabled: authenticated,
  });

  useEffect(() => {
    let getEmployeeError = '';
    if (getIncompleteEmployee.isSuccess && getIncompleteEmployee.data) {
      updateCompanyId(getIncompleteEmployee.data.companyId);
    } else if (getIncompleteEmployee.isSuccess && !getIncompleteEmployee.data) {
      getEmployeeError = 'completeProfileAfterRegistration.noEmployeeFound.message';
    } else if (getIncompleteEmployee.isError) {
      getEmployeeError = resolveCommonError(getIncompleteEmployee.error);
    }
    // Mutation inside useEffect is acceptable here because error state depends on async mutation result
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(getEmployeeError);
  }, [getIncompleteEmployee, updateCompanyId]);

  return getIncompleteEmployee.isSuccess && getIncompleteEmployee.data ? (
    <CompleteDataForm employeeId={getIncompleteEmployee.data.id} />
  ) : (
    <>
      <MessageLayout
        icon={!error && <CircularProgress size={32} />}
        title={t('completeProfileAfterRegistration.title')}
        text={error ? t(error) : t('completeProfileAfterRegistration.message')}
      />
      <ErrorDialog
        open={!!error}
        errorKey={error}
        title={t('completeProfileAfterRegistration.noEmployeeFound.title')}
        body={t(error!)}
        primaryAction={
          <>
            {getIncompleteEmployee.isSuccess && !getIncompleteEmployee.data ? (
              <Button href={resolveUrl(APP_URL)} role="link">
                {t('checkRegistrationLink.actions.login', { app: USE_BLS_THEME ? 'Bikeleasing' : 'Probonio' })}
              </Button>
            ) : (
              <Button onClick={loginAgain}>{t('errorDialog.actions.loginAgain')}</Button>
            )}
          </>
        }
      />
    </>
  );
}
